import { LoadingCards } from '@eventbrite/eds-event-card';
import { SearchRetrievalContextProvider } from '@eventbrite/event-tracker';
import {
    trackAnalyticsEvent,
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import { History } from 'history';
import React, { FocusEventHandler, MouseEventHandler } from 'react';
import { connect } from 'react-redux';
import { useMount } from 'react-use';
import { SearchEventCardStyleSwitcher } from '../../../../components/EventCard';
import {
    GA_SEARCH_CATEGORY,
    SEARCH_EVENT_NAVIGATION,
    SEARCH_SECTION,
} from '../../../../constants/analytics';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { getEventExpansions as getEventExpansionsAction } from '../../../../redux/actions/expansions';
import { setHoverEventTarget as setHoverEventTargetAction } from '../../../../redux/actions/ui';
import { Events, FeatureFlags } from '../../../../types/index';
import { getSearchContent, getSearchSection } from '../../../../utils/general';
import { addSearchIdToGaAction } from '../../utils/analytics';
import { hasEventResults } from '../../utils/events';
import { SearchHeaderContainer } from '../SearchHeaderContainer';
import './SearchResultsPanelContainer.scss';

const LazyTopMatchContainer = loadable(() => import('../TopMatchContainer'), {
    resolveComponent: (components: { TopMatchContainer: React.ReactElement }) =>
        components.TopMatchContainer,
});

const LIST_TYPE = 'list';

interface SearchResultsProps {
    content?: Events;
    history?: History;
    onMouseOverCard?: Function;
    onMouseOutCard?: MouseEventHandler<HTMLDivElement> &
        FocusEventHandler<HTMLDivElement>;
    trackClick?: Function;
    trackClickInternal?: Function;
    trackGAEvent?: Function;
    isAuthenticated?: boolean;
    isOnline?: boolean;
    isMobile?: boolean;
    featureFlags?: FeatureFlags;
    shouldShowDebugOutput?: boolean;
}

const SearchResults = (props: SearchResultsProps) => {
    useMount(() => {
        const checkForDedup = ({
            dedup,
        }: {
            dedup?: { count: number; hash?: string };
        }) => dedup && typeof dedup.count === 'number' && dedup.count > 1;
        const hasDedupedEvent = props.content?.some((event) =>
            checkForDedup(event),
        );

        if (hasDedupedEvent) {
            trackAnalyticsEvent({
                category: 'search',
                action: 'DeDupedResultsShown',
            });
        }
    });

    return (
        <ul className="search-main-content__events-list">
            {props.content?.map(({ id, ...cardProps }: any, index: number) => (
                <li key={id}>
                    <div
                        className="search-event-card-wrapper"
                        onMouseEnter={props.onMouseOverCard?.bind(null, id)}
                        onFocus={props.onMouseOverCard?.bind(null, id)}
                        onMouseLeave={props.onMouseOutCard}
                        onBlur={props.onMouseOutCard}
                        role="presentation"
                    >
                        <SearchEventCardStyleSwitcher
                            id={id}
                            trackClick={props.trackClick?.bind(null, index, id)}
                            type={LIST_TYPE}
                            isAuthenticated={props.isAuthenticated}
                            containerSpacingClasses={
                                props.isMobile ? '' : 'eds-l-pad-all-4'
                            }
                            trackClickInternal={props.trackClickInternal?.bind(
                                null,
                                index,
                            )}
                            trackGAEvent={props.trackGAEvent}
                            section={SEARCH_SECTION}
                            featureFlags={props.featureFlags}
                            position={index + 1}
                            shouldShowDebugOutput={props.shouldShowDebugOutput}
                            history={props.history}
                            {...cardProps}
                        />
                    </div>
                </li>
            ))}
        </ul>
    );
};

/**
 * Depending on the loading state of the application
 * will render the search results themselves or the
 * the appropriate loading state.
 * */
interface SearchResultsControllerProps {
    isLoading?: boolean;
    isMobile?: boolean;
    isOnline?: boolean;
    results?: {
        content: Events;
        type: string;
    }[];
}

export const SearchResultsController = ({
    isLoading,
    isMobile,
    ...props
}: SearchResultsControllerProps) => {
    let component = (
        <div className="search-main-content">
            <LoadingCards style={isMobile ? 'list-square' : 'list'} />
        </div>
    );

    if (!isLoading) {
        component = (
            <div className="search-main-content">
                {props.results?.map(({ content, type }) => {
                    const key = `search_${type}`;
                    const emptyResults = <div key={key} />;

                    if (!content) {
                        return emptyResults;
                    }

                    return (
                        <SearchResults {...props} content={content} key={key} />
                    );
                })}
            </div>
        );
    }

    return component;
};

const SearchResultSection = ({
    searchResultProps,
    isLoading,
    hasEventResults,
    isMobile,
}: {
    allEventsUrl?: string;
    featureFlags?: FeatureFlags;
    isAuthenticated?: boolean;
    isLoading?: boolean;
    isMobile?: boolean;
    hasEventResults: boolean;
    searchResultProps: SearchResultsProps;
    trackGAEvent?: Function;
    trackGAEventOnPageUnload?: Function;
}) => {
    let component: React.ReactElement | null = (
        <SearchResultsController {...searchResultProps} />
    );

    if (isMobile) {
        component = (
            <SearchResultsController {...searchResultProps} isMobile={true} />
        );
    }

    if (!isLoading && !hasEventResults) {
        component = null;
    }

    return component;
};

interface ResultsPanelProps {
    allEventsUrl?: string;
    currentPage?: number;
    featureFlags?: FeatureFlags;
    getEventExpansions: Function;
    hasEventResults?: boolean;
    hasTopMatch?: boolean;
    history?: History;
    isAuthenticated?: boolean;
    isLoading?: boolean;
    onGoogleMapsLoaded?: Function;
    onTrackEventClickInternal?: Function;
    results?: object[];
    setHoverEventTarget?: Function;
    shouldShowDebugOutput?: boolean;
    trackGAEvent?: Function;
    trackGAEventOnPageUnload?: Function;
    eventSearch: object;
    searchId: string;
}

export class ResultsPanel extends React.PureComponent<ResultsPanelProps> {
    componentDidMount() {
        this.props.getEventExpansions();
    }

    _handleTrackClick = (
        index: number,
        eventId: string,
        action: string,
        organizerFollowerCount: number,
    ) => {
        const { trackGAEvent } = this.props;

        trackGAEvent?.({
            action,
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: index,
                organizerFollowerCount: organizerFollowerCount || 0,
            },
        });
    };

    _handleTrackClickInternal = (
        index: number,
        id: string,
        ticketsBy: string,
    ) => {
        const { onTrackEventClickInternal } = this.props;

        const trackingData = {
            clickRank: index,
            navigation: SEARCH_EVENT_NAVIGATION,
            itemId: id,
        };

        onTrackEventClickInternal?.(trackingData, ticketsBy);
    };

    _handleMouseOverCard = (eid: string) => {
        this.props.setHoverEventTarget?.(eid);
    };

    _handleMouseOutCard = () => {
        this.props.setHoverEventTarget?.(null);
    };

    render() {
        const {
            allEventsUrl,
            featureFlags,
            hasEventResults,
            history,
            isAuthenticated,
            isLoading,
            results,
            shouldShowDebugOutput,
            trackGAEvent,
            trackGAEventOnPageUnload,
            hasTopMatch,
            currentPage,
            eventSearch,
            searchId,
        } = this.props;

        const searchResultProps = {
            featureFlags,
            history,
            isAuthenticated,
            isLoading,
            onMouseOutCard: this._handleMouseOutCard,
            onMouseOverCard: this._handleMouseOverCard,
            results,
            shouldShowDebugOutput,
            trackClick: this._handleTrackClick,
            trackClickInternal: this._handleTrackClickInternal,
            trackGAEvent,
            eventSearch,
        };

        const renderTopMatch = hasTopMatch && !isLoading && currentPage === 0;

        return (
            <div className="search-results-panel-content">
                <SearchRetrievalContextProvider
                    eventSearch={eventSearch}
                    searchId={searchId}
                >
                    <SearchHeaderContainer history={this.props.history} />
                    {renderTopMatch && <LazyTopMatchContainer />}
                    <SearchResultSection
                        allEventsUrl={allEventsUrl}
                        featureFlags={featureFlags}
                        hasEventResults={hasEventResults || false}
                        isAuthenticated={isAuthenticated}
                        isLoading={isLoading}
                        searchResultProps={searchResultProps}
                        trackGAEvent={trackGAEvent}
                        trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                    />
                </SearchRetrievalContextProvider>
            </div>
        );
    }
}

export const _mapStateToProps = (state: any, ownProps: any) => {
    const section = getSearchSection(state.location.isOnline);
    const results = getSearchContent(
        state.content.entities,
        state.pagination,
        state.currencyFormat,
        section,
    );

    const hasTopMatch = state.content.entityContext.some(
        (context: { topMatchIds: string[] }) =>
            !!context.topMatchIds && context.topMatchIds.length > 0,
    );

    return {
        results,
        history: ownProps.history,
        currentPage: state.pagination.currentPage,
        hasTopMatch,
        hasEventResults: hasEventResults(results),
        isAuthenticated: state.auth.isAuthenticated,
        isLoading: state.pagination.loading,
        featureFlags: state.app.featureFlags,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        eventSearch: state.search.eventSearch,
        searchId: state.search.searchId,
        trackGAEvent: (action: any) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        trackGAEventOnPageUnload: (action: any) =>
            trackEventFromStateOnPageUnload(
                state,
                addSearchIdToGaAction(
                    {
                        ...action,
                        category: GA_SEARCH_CATEGORY,
                    },
                    state,
                ),
            ),
    };
};

const _mapDispatchToProps = (dispatch: Function) => ({
    setHoverEventTarget: (...args: [$FixMe]) =>
        dispatch(setHoverEventTargetAction(...args)),
    onTrackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
    getEventExpansions: () => dispatch(getEventExpansionsAction()),
});

export const SearchResultsPanelContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(ResultsPanel);
